/**
 * How a thread update's value reads. The panel diffs every catalogue field a
 * reply states (stress test LPR-04), not only the ten it used to — so a fee
 * in basis points, a term in months or a year built must not print as money
 * ('Origination Fee Bps + $100', 'Year Built $1,928').
 */

const PERCENT_FIELDS = new Set([
  'cap_rate', 'cap_rate_stabilized', 'occupancy', 'ltv', 'ltv_stabilized', 'ltc',
  'debt_yield', 'debt_yield_stabilized', 'exit_cap', 'floor', 'levered_irr',
  'unlevered_irr', 'preferred_return', 'yield_on_cost', 'promote', 'irr_hurdle',
])

const PLAIN_NUMBER_FIELDS = new Set([
  'year_built', 'year_renovated', 'units', 'sf', 'stories', 'parking_spaces', 'keys',
])

const MONEY_HINTS = ['amount', 'value', 'noi', 'ncf', 'revenue', 'expenses', 'debt_service',
  'cost', 'equity', 'funding', 'price', 'proceeds', 'reserve', 'appraised']

export function fmtThreadValue(field: string, v: unknown): string {
  const snake = field.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)
  if (typeof v !== 'number') return v == null ? '—' : String(v)
  if (PERCENT_FIELDS.has(snake)) {
    return `${(v <= 1 ? v * 100 : v).toFixed(2).replace(/\.?0+$/, '')}%`
  }
  if (snake === 'dscr' || snake.endsWith('_dscr')) return `${v.toFixed(2)}x`
  if (snake.endsWith('_bps')) return `${v.toLocaleString('en-US')} bps`
  if (snake.endsWith('_months')) return `${v} mo`
  if (PLAIN_NUMBER_FIELDS.has(snake)) return String(v)
  if (MONEY_HINTS.some((hint) => snake.includes(hint))) return `$${v.toLocaleString('en-US')}`
  return v.toLocaleString('en-US')
}

/** What matched a re-ingested record to its deal, said as what it is. Every
 *  match used to read 'CONTROL-ID MATCH', including a name match between two
 *  records with no Control ID (stress test PKG-01). A block stored before the
 *  basis was recorded still says 'control_id_exact' whatever matched; a Control
 *  ID match names the Control ID, so one that names none is not read as one. */
export function reingestBasisLabel(basis: string | null | undefined, controlId?: string | null): string {
  switch (basis) {
    case 'control_id_exact':
      return controlId ? `Control ID ${controlId}` : 'match basis not recorded'
    case 'property_name_exact':
      return 'same property name'
    case 'address_exact':
      return 'same street address'
    case 'manual_deal_property_name':
    case 'manual_deal_property_name_exact':
      return 'same name as a deal entered by hand'
    case 'manual_deal_property_name_similar':
      return 'similar name to a deal entered by hand'
    case 'manual_deal_address':
    case 'manual_deal_address_exact':
      return 'same address as a deal entered by hand'
    case 'deal_group':
      return 'same deal group'
    default:
      return controlId ? `Control ID ${controlId}` : 'matched deal'
  }
}

/** Field (snake case) -> why an OLDER email's figure changed nothing: a newer
 *  email already put the deal's value there, so the older one is its history
 *  and is never offered as though it simply disagreed with the loan. */
export function supersededNotes(
  updates: { superseded?: { field: string; why: string }[] } | null | undefined,
): Map<string, string> {
  const snake = (k: string) => k.replace(/[A-Z]/g, (m) => `_${m.toLowerCase()}`)
  return new Map((updates?.superseded ?? []).map((s) => [snake(s.field), s.why]))
}
